<template>
  <div class="automations-editor">
    <header class="automations-editor__header">
      <Btn variant="plain" icon="arrow-left" type="button" @click="goBack">
        {{ $t('automations_page.back') }}
      </Btn>
      <div class="automations-editor__title">
        <InplaceEdit
          :label="currentAutomation.name"
          :value="currentAutomation.name"
          @update="updateField('name', $event)"
        />
      </div>
      <Label :variant="statusVariant" size="sm" show-dot>
        {{ statusLabel }}
      </Label>
      <ToggleInput
        :model-value="currentAutomation.status === 'active'"
        @update:model-value="updateStatus"
        :label="$t('automations_page.active')"
        id="is-active-toggle"
        :disabled="!currentAutomation.id"
      />
      <div class="automations-editor__actions">
        <Btn
          :variant="showRuns ? 'secondary' : 'outlined'"
          type="button"
          @click="toggleRuns"
        >
          {{ $t('automations_page.runs') }}
        </Btn>
        <Btn
          variant="primary"
          :disabled="!hasUnsavedChanges"
          :loading="isSaving"
          type="button"
          @click="handleSaveAutomation"
        >
          {{ $t('automations_page.save') }}
        </Btn>
      </div>
    </header>

    <div class="automations-editor__view">
      <AutomationBuilder
        v-if="currentAutomation && !isLoading"
        :automation="currentAutomation"
        :run-status="selectedExecution?.status || null"
        :run-steps="selectedExecution?.steps || []"
        @update="handleAutomationUpdate"
        @save="handleSaveAutomation"
      />
      <AutomationRunsPanel
        v-if="showRuns"
        :runs="executions"
        :selected-run="selectedExecution"
        :loading="executionsLoading"
        @select="selectExecution"
        @clear="clearExecution"
      />
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, getCurrentInstance } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { debounce } from 'lodash-es';
  import Btn from '@/components/partials/Btn.vue';
  import Label from '@/components/partials/Label.vue';
  import { useAutomationStore } from '@/store/automationStore';
  import { useToastStore } from '@/store/toastStore';
  import AutomationBuilder from '@/components/automations/AutomationBuilder.vue';
  import AutomationRunsPanel from '@/components/automations/AutomationRunsPanel.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import InplaceEdit from '@/components/partials/InplaceEdit.vue';

  export default {
    name: 'AutomationsView',

    components: {
      Btn,
      Label,
      AutomationBuilder,
      AutomationRunsPanel,
      InplaceEdit,
      ToggleInput,
    },

    setup() {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const automationStore = useAutomationStore();
      const toastStore = useToastStore();
      const route = useRoute();
      const router = useRouter();
      const isSaving = ref(false);
      const automationId = computed(() => route.params.id);
      const isLoading = ref(true);
      const showRuns = ref(false);
      const selectedExecution = ref(null);
      const originalAutomation = ref(null);
      const currentAutomation = ref({
        id: null,
        organization_id: null,
        name: '',
        trigger_type: null,
        trigger_details: {},
        created_by: null,
        updated_by: null,
        created_at: null,
        updated_at: null,
        description: null,
        status: 'active',
        steps: [],
        form_id: null,
        group_id: null,
        sort_order: 0,
      });

      const executions = computed(() => automationStore.executions);
      const executionsLoading = computed(() => automationStore.executionsLoading);

      const statusLabel = computed(() => {
        const status = currentAutomation.value.status || 'draft';
        return $t(`automations_page.status.${status}`) || status;
      });

      const statusVariant = computed(() => {
        if (currentAutomation.value.status === 'active') return 'success';
        if (currentAutomation.value.status === 'inactive') return 'default';
        return 'warning';
      });

      const hasUnsavedChanges = computed(() => {
        if (!originalAutomation.value) return false;

        const compareOriginal = JSON.parse(JSON.stringify(originalAutomation.value));
        const compareCurrent = JSON.parse(JSON.stringify(currentAutomation.value));

        delete compareOriginal.updated_at;
        delete compareCurrent.updated_at;

        return JSON.stringify(compareCurrent) !== JSON.stringify(compareOriginal);
      });

      const handleAutomationUpdate = debounce((newAutomation) => {
        const clonedAutomation = JSON.parse(JSON.stringify(newAutomation));

        clonedAutomation.steps = clonedAutomation.steps.map((step) => {
          const existingStep = currentAutomation.value?.steps?.find((es) => es.id === step.id);

          return {
            ...step,
            action_details: step.action_details || existingStep?.action_details || {},
            configuration: step.configuration || existingStep?.configuration || {},
          };
        });

        if (JSON.stringify(currentAutomation.value) === JSON.stringify(clonedAutomation)) {
          return;
        }

        currentAutomation.value = clonedAutomation;
      }, 300);

      const sanitizeAutomationForSave = (automation) => {
        const cloned = JSON.parse(JSON.stringify(automation));
        cloned.steps = (cloned.steps || []).map((step) => {
          if (step.action_type !== 'integration') return step;
          const details = { ...(step.action_details || {}) };
          if (Array.isArray(details.event_properties)) {
            details.event_properties = details.event_properties.filter((p) => p?.key);
          }
          if (Array.isArray(details.mailing_lists)) {
            details.mailing_lists = details.mailing_lists.filter((m) => m?.list_id || m?.listId);
          }
          if (Array.isArray(details.contactFields)) {
            details.contactFields = details.contactFields.filter(
              (f) => f?.acFieldId || f?.ac_field_id
            );
          }
          return { ...step, action_details: details };
        });
        return cloned;
      };

      const handleSaveAutomation = async () => {
        if (!hasUnsavedChanges.value) return;

        try {
          isSaving.value = true;
          const payload = sanitizeAutomationForSave(currentAutomation.value);
          await automationStore.updateAutomation(payload);
          currentAutomation.value = JSON.parse(JSON.stringify(payload));
          originalAutomation.value = JSON.parse(JSON.stringify(payload));
        } catch (error) {
          console.error('Failed to save automation:', error);
          toastStore.addToast({
            type: 'error',
            message: getSaveErrorMessage(error),
            duration: 5000,
          });
        } finally {
          isSaving.value = false;
        }
      };

      const getSaveErrorMessage = (error) => {
        const body = error?.response?.data;
        const code = body?.error;
        if (code === 'invalid_duration') {
          return 'Een delay-stap heeft een ongeldige duur. Vul een geheel getal van minimaal 1 in.';
        }
        if (code === 'invalid_unit') {
          const allowed = Array.isArray(body?.allowed)
            ? body.allowed.join(', ')
            : 'minutes, hours, days, weeks';
          return `Een delay-stap gebruikt een ongeldige eenheid. Toegestaan: ${allowed}.`;
        }
        if (code === 'delay_too_long') {
          return 'Een delay-stap is langer dan toegestaan (max. 1 jaar). Splits hem op in twee delays.';
        }
        return body?.message || 'Kon automation niet opslaan. Probeer het opnieuw.';
      };

      const updateStatus = async (value) => {
        if (!currentAutomation.value.id) return;

        const targetStatus = value ? 'active' : 'inactive';

        try {
          const response = await automationStore.toggleAutomation(
            currentAutomation.value.id,
            targetStatus
          );
          const updatedAutomation = response?.data;

          if (updatedAutomation) {
            currentAutomation.value = {
              ...currentAutomation.value,
              status: updatedAutomation.status,
            };
            originalAutomation.value = {
              ...originalAutomation.value,
              status: updatedAutomation.status,
            };
          }
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('automations_page.toggle_failed'),
          });
        }
      };

      const updateField = (field, value) => {
        currentAutomation.value[field] = value;
      };

      const toggleRuns = async () => {
        showRuns.value = !showRuns.value;
        if (showRuns.value && currentAutomation.value.id) {
          try {
            await automationStore.fetchExecutions(currentAutomation.value.id);
          } catch (error) {
            toastStore.addToast({
              type: 'error',
              message: 'Kon runs niet laden',
            });
          }
        }
      };

      const selectExecution = async (run) => {
        try {
          const detail = await automationStore.fetchExecution(
            currentAutomation.value.id,
            run.id
          );
          selectedExecution.value = detail;
        } catch (error) {
          selectedExecution.value = run;
        }
      };

      const clearExecution = () => {
        selectedExecution.value = null;
      };

      const goBack = () => {
        router.push('/automations');
      };

      onMounted(async () => {
        if (automationId.value) {
          try {
            const response = await automationStore.fetchAutomation(automationId.value);
            if (response) {
              const clonedResponse = JSON.parse(JSON.stringify(response));
              currentAutomation.value = clonedResponse;
              originalAutomation.value = clonedResponse;
              isLoading.value = false;
            }
          } catch (error) {
            console.error('Failed to fetch automation:', error);
          }
        }
      });

      return {
        currentAutomation,
        handleSaveAutomation,
        isSaving,
        hasUnsavedChanges,
        handleAutomationUpdate,
        isLoading,
        updateStatus,
        updateField,
        showRuns,
        executions,
        executionsLoading,
        selectedExecution,
        toggleRuns,
        selectExecution,
        clearExecution,
        goBack,
        statusLabel,
        statusVariant,
      };
    },
  };
</script>

<style scoped lang="scss">
  .automations-editor {
    height: 100%;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #fafafa;

    &__header {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem 1.25rem;
      background: #fff;
      border-bottom: 1px solid var(--slate-200);
    }

    &__title {
      flex: 1;
      min-width: 0;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-left: auto;
    }

    &__view {
      flex-grow: 1;
      overflow: hidden;
      display: flex;
    }
  }
</style>
